/**
 * Pure investor-calculator math.
 *
 * Every standalone calculator route imports its formulas from here so the
 * numbers are unit-testable and identical across pages. Nothing in this module
 * reads lender pricing, quotes a rate, or implies an approval — callers supply
 * every assumption and the UI labels the output as an illustrative estimate.
 */

const round2 = (n: number) => Math.round(n * 100) / 100;
const round4 = (n: number) => Math.round(n * 10000) / 10000;
const safe = (n: number) => (Number.isFinite(n) ? n : 0);

/** Level monthly payment for a fully amortizing loan. */
export function monthlyPayment(principal: number, annualRatePct: number, years: number): number {
  const p = Math.max(0, safe(principal));
  const n = Math.max(1, Math.round(safe(years) * 12));
  const r = safe(annualRatePct) / 100 / 12;
  if (p === 0) return 0;
  if (r <= 0) return round2(p / n);
  const pmt = (p * r) / (1 - Math.pow(1 + r, -n));
  return round2(pmt);
}

/** Interest-only monthly payment. */
export function interestOnlyPayment(principal: number, annualRatePct: number): number {
  return round2((Math.max(0, safe(principal)) * safe(annualRatePct)) / 100 / 12);
}

/** Remaining balance after `monthsElapsed` payments on an amortizing loan. */
export function remainingBalance(
  principal: number,
  annualRatePct: number,
  years: number,
  monthsElapsed: number,
): number {
  const p = Math.max(0, safe(principal));
  const n = Math.max(1, Math.round(safe(years) * 12));
  const m = Math.min(Math.max(0, Math.round(safe(monthsElapsed))), n);
  const r = safe(annualRatePct) / 100 / 12;
  if (p === 0) return 0;
  if (r <= 0) return round2(p * (1 - m / n));
  const pmt = (p * r) / (1 - Math.pow(1 + r, -n));
  const bal = p * Math.pow(1 + r, m) - pmt * ((Math.pow(1 + r, m) - 1) / r);
  return round2(Math.max(0, bal));
}

/** Total interest paid over `months` on an amortizing loan. */
export function interestPaid(
  principal: number,
  annualRatePct: number,
  years: number,
  months: number,
): number {
  const pmt = monthlyPayment(principal, annualRatePct, years);
  const m = Math.max(0, Math.round(safe(months)));
  const end = remainingBalance(principal, annualRatePct, years, m);
  return round2(pmt * m - (Math.max(0, safe(principal)) - end));
}

// ---------------------------------------------------------------- PITIA

export type PitiaInput = {
  loanAmount: number;
  ratePct: number;
  termYears: number;
  interestOnly?: boolean;
  taxesAnnual: number;
  insuranceAnnual: number;
  floodAnnual?: number;
  hoaMonthly?: number;
};

export type PitiaResult = {
  pi: number;
  taxes: number;
  insurance: number;
  flood: number;
  hoa: number;
  pitia: number;
};

/** Monthly principal+interest, taxes, insurance, (flood) and HOA/association dues. */
export function calcPitia(i: PitiaInput): PitiaResult {
  const pi = i.interestOnly
    ? interestOnlyPayment(i.loanAmount, i.ratePct)
    : monthlyPayment(i.loanAmount, i.ratePct, i.termYears);
  const taxes = round2(Math.max(0, safe(i.taxesAnnual)) / 12);
  const insurance = round2(Math.max(0, safe(i.insuranceAnnual)) / 12);
  const flood = round2(Math.max(0, safe(i.floodAnnual ?? 0)) / 12);
  const hoa = round2(Math.max(0, safe(i.hoaMonthly ?? 0)));
  return { pi, taxes, insurance, flood, hoa, pitia: round2(pi + taxes + insurance + flood + hoa) };
}

// -------------------------------------------------- Rent needed for a DSCR

export type RentNeededResult = {
  requiredMonthlyRent: number;
  requiredAnnualRent: number;
  /** Rent before any vacancy/management haircut the user applied. */
  requiredGrossRent: number;
  currentDscr: number | null;
  gapMonthly: number | null;
};

/**
 * Qualifying rent required to hit a target DSCR.
 *
 * DSCR = qualifying rent / PITIA, so qualifying rent = PITIA x target.
 * `haircutPct` grosses the answer back up when the program underwrites net of
 * vacancy or management (most DSCR programs use gross market rent — the field
 * defaults to zero).
 */
export function rentNeededForDscr(
  pitia: number,
  targetDscr: number,
  currentRent?: number,
  haircutPct = 0,
): RentNeededResult {
  const p = Math.max(0, safe(pitia));
  const t = Math.max(0, safe(targetDscr));
  const required = round2(p * t);
  const h = Math.min(Math.max(0, safe(haircutPct)), 90) / 100;
  const gross = round2(h > 0 ? required / (1 - h) : required);
  const rent = currentRent !== undefined ? Math.max(0, safe(currentRent)) : undefined;
  return {
    requiredMonthlyRent: required,
    requiredAnnualRent: round2(required * 12),
    requiredGrossRent: gross,
    currentDscr: rent !== undefined && p > 0 ? round2((rent * (1 - h)) / p) : null,
    gapMonthly: rent !== undefined ? round2(gross - rent) : null,
  };
}

// ------------------------------------------------------------- LTV / equity

export type LtvResult = {
  ltv: number;
  cltv: number;
  equity: number;
  equityPct: number;
  maxLoanAtTarget: number;
  availableAtTarget: number;
};

/** LTV, CLTV, equity, and headroom at a target LTV/CLTV. */
export function calcLtv(
  value: number,
  firstLien: number,
  secondLien = 0,
  targetLtvPct = 75,
): LtvResult {
  const v = Math.max(0, safe(value));
  const first = Math.max(0, safe(firstLien));
  const second = Math.max(0, safe(secondLien));
  const total = first + second;
  const maxLoan = round2(v * (Math.max(0, safe(targetLtvPct)) / 100));
  return {
    ltv: v > 0 ? round2(first / v) : 0,
    cltv: v > 0 ? round2(total / v) : 0,
    equity: round2(v - total),
    equityPct: v > 0 ? round2((v - total) / v) : 0,
    maxLoanAtTarget: maxLoan,
    availableAtTarget: round2(Math.max(0, maxLoan - total)),
  };
}

// --------------------------------------------------------- Prepay penalties

export type PrepayStructure =
  | { kind: "none" }
  | { kind: "stepdown"; schedule: number[] }
  | { kind: "fixed"; percent: number; years: number }
  | { kind: "yield-maintenance-flat"; percent: number; years: number };

/** Penalty percentage that applies when the loan pays off in `year` (1-based). */
export function prepayPenaltyPercent(structure: PrepayStructure, year: number): number {
  const y = Math.max(1, Math.ceil(safe(year)));
  switch (structure.kind) {
    case "none":
      return 0;
    case "stepdown":
      return y <= structure.schedule.length ? Math.max(0, safe(structure.schedule[y - 1] ?? 0)) : 0;
    case "fixed":
    case "yield-maintenance-flat":
      return y <= structure.years ? Math.max(0, safe(structure.percent)) : 0;
  }
}

/**
 * Dollar penalty on a payoff in `year`.
 * `basis` is the balance the penalty is charged against (original balance for
 * some programs, current balance for others — the caller decides).
 */
export function prepayPenaltyCost(
  structure: PrepayStructure,
  year: number,
  basis: number,
): number {
  return round2((Math.max(0, safe(basis)) * prepayPenaltyPercent(structure, year)) / 100);
}

/** Standard step-down shorthand, e.g. "5/4/3/2/1" -> [5,4,3,2,1]. */
export function parseStepdown(text: string): number[] {
  return String(text)
    .split(/[^0-9.]+/)
    .filter(Boolean)
    .map((n) => Math.max(0, parseFloat(n) || 0))
    .slice(0, 10);
}

// ------------------------------------------------- IO vs amortizing compare

export type IoCompare = {
  ioPayment: number;
  amortPayment: number;
  monthlyDifference: number;
  ioCashFlow: number;
  amortCashFlow: number;
  ioDscr: number | null;
  amortDscr: number | null;
  ioInterestOverPeriod: number;
  amortInterestOverPeriod: number;
  amortPrincipalPaid: number;
  ioBalanceAtEnd: number;
  amortBalanceAtEnd: number;
};

/**
 * Interest-only vs fully amortizing over a hold period.
 * `otherMonthly` = taxes + insurance + HOA (the non-P&I part of PITIA), used so
 * DSCR is computed on the full housing expense.
 */
export function compareIoVsAmortizing(args: {
  loanAmount: number;
  ratePct: number;
  termYears: number;
  holdYears: number;
  monthlyRent: number;
  otherMonthly: number;
  operatingExpensesMonthly?: number;
}): IoCompare {
  const months = Math.max(1, Math.round(safe(args.holdYears) * 12));
  const io = interestOnlyPayment(args.loanAmount, args.ratePct);
  const am = monthlyPayment(args.loanAmount, args.ratePct, args.termYears);
  const other = Math.max(0, safe(args.otherMonthly));
  const opex = Math.max(0, safe(args.operatingExpensesMonthly ?? 0));
  const rent = Math.max(0, safe(args.monthlyRent));
  const ioPitia = io + other;
  const amPitia = am + other;
  const endBal = remainingBalance(args.loanAmount, args.ratePct, args.termYears, months);
  return {
    ioPayment: io,
    amortPayment: am,
    monthlyDifference: round2(am - io),
    ioCashFlow: round2(rent - ioPitia - opex),
    amortCashFlow: round2(rent - amPitia - opex),
    ioDscr: ioPitia > 0 ? round2(rent / ioPitia) : null,
    amortDscr: amPitia > 0 ? round2(rent / amPitia) : null,
    ioInterestOverPeriod: round2(io * months),
    amortInterestOverPeriod: interestPaid(args.loanAmount, args.ratePct, args.termYears, months),
    amortPrincipalPaid: round2(Math.max(0, safe(args.loanAmount)) - endBal),
    ioBalanceAtEnd: round2(Math.max(0, safe(args.loanAmount))),
    amortBalanceAtEnd: endBal,
  };
}

// ----------------------------------------------------- Closing / cash to close

export type ClosingCostInput = {
  purchasePrice: number;
  downPaymentPct: number;
  originationPct: number;
  lenderFlatFees: number;
  titleAndSettlement: number;
  appraisal: number;
  recordingAndTransferPct: number;
  prepaidInsuranceMonths: number;
  insuranceAnnual: number;
  prepaidTaxMonths: number;
  taxesAnnual: number;
  escrowReserveMonths: number;
  monthlyPitia: number;
  earnestMoney: number;
  sellerCredits: number;
};

export type ClosingCostResult = {
  loanAmount: number;
  downPayment: number;
  origination: number;
  recordingAndTransfer: number;
  prepaidInsurance: number;
  prepaidTaxes: number;
  escrowReserves: number;
  thirdPartyFees: number;
  totalClosingCosts: number;
  totalPrepaidsAndReserves: number;
  estimatedCashToClose: number;
};

/** Estimated closing costs and cash to close. Every input is user-editable. */
export function calcClosingCosts(i: ClosingCostInput): ClosingCostResult {
  const price = Math.max(0, safe(i.purchasePrice));
  const down = round2(price * (Math.max(0, safe(i.downPaymentPct)) / 100));
  const loan = round2(price - down);
  const origination = round2(loan * (Math.max(0, safe(i.originationPct)) / 100));
  const recording = round2(price * (Math.max(0, safe(i.recordingAndTransferPct)) / 100));
  const prepaidIns = round2(
    (Math.max(0, safe(i.insuranceAnnual)) / 12) * Math.max(0, safe(i.prepaidInsuranceMonths)),
  );
  const prepaidTax = round2(
    (Math.max(0, safe(i.taxesAnnual)) / 12) * Math.max(0, safe(i.prepaidTaxMonths)),
  );
  const reserves = round2(
    Math.max(0, safe(i.monthlyPitia)) * Math.max(0, safe(i.escrowReserveMonths)),
  );
  const thirdParty = round2(
    Math.max(0, safe(i.titleAndSettlement)) + Math.max(0, safe(i.appraisal)) + recording,
  );
  const closing = round2(origination + Math.max(0, safe(i.lenderFlatFees)) + thirdParty);
  const prepaids = round2(prepaidIns + prepaidTax + reserves);
  const cash = round2(
    down + closing + prepaids - Math.max(0, safe(i.earnestMoney)) - Math.max(0, safe(i.sellerCredits)),
  );
  return {
    loanAmount: loan,
    downPayment: down,
    origination,
    recordingAndTransfer: recording,
    prepaidInsurance: prepaidIns,
    prepaidTaxes: prepaidTax,
    escrowReserves: reserves,
    thirdPartyFees: thirdParty,
    totalClosingCosts: closing,
    totalPrepaidsAndReserves: prepaids,
    estimatedCashToClose: cash,
  };
}

// ------------------------------------------------------- Cash-out refinance

export type CashOutInput = {
  propertyValue: number;
  currentBalance: number;
  targetLtvPct: number;
  newRatePct: number;
  newTermYears: number;
  interestOnly?: boolean;
  currentPitia: number;
  currentPi: number;
  monthlyRent: number;
  otherMonthly: number;
  closingCostPct: number;
  closingCostFlat: number;
  reinvestReturnPct: number;
};

export type CashOutResult = {
  maxLoan: number;
  newLoanAmount: number;
  grossProceeds: number;
  closingCosts: number;
  netProceeds: number;
  newPi: number;
  newPitia: number;
  paymentChange: number;
  newDscr: number | null;
  currentDscr: number | null;
  monthlyCashFlowChange: number;
  reinvestMonthlyIncome: number;
  breakEvenMonths: number | null;
};

/** Cash-out refinance proceeds, new debt service, DSCR impact and break-even. */
export function calcCashOutRefi(i: CashOutInput): CashOutResult {
  const value = Math.max(0, safe(i.propertyValue));
  const balance = Math.max(0, safe(i.currentBalance));
  const maxLoan = round2(value * (Math.max(0, safe(i.targetLtvPct)) / 100));
  const newLoan = maxLoan;
  const gross = round2(Math.max(0, newLoan - balance));
  const costs = round2(newLoan * (Math.max(0, safe(i.closingCostPct)) / 100) + Math.max(0, safe(i.closingCostFlat)));
  const net = round2(gross - costs);
  const newPi = i.interestOnly
    ? interestOnlyPayment(newLoan, i.newRatePct)
    : monthlyPayment(newLoan, i.newRatePct, i.newTermYears);
  const other = Math.max(0, safe(i.otherMonthly));
  const newPitia = round2(newPi + other);
  const rent = Math.max(0, safe(i.monthlyRent));
  const currentPitia = Math.max(0, safe(i.currentPitia));
  const paymentChange = round2(newPitia - currentPitia);
  const reinvest = round2((Math.max(0, net) * (Math.max(0, safe(i.reinvestReturnPct)) / 100)) / 12);
  const cashFlowChange = round2(-paymentChange);
  const monthlyBenefit = round2(reinvest + cashFlowChange);
  return {
    maxLoan,
    newLoanAmount: newLoan,
    grossProceeds: gross,
    closingCosts: costs,
    netProceeds: net,
    newPi,
    newPitia,
    paymentChange,
    newDscr: newPitia > 0 ? round2(rent / newPitia) : null,
    currentDscr: currentPitia > 0 ? round2(rent / currentPitia) : null,
    monthlyCashFlowChange: cashFlowChange,
    reinvestMonthlyIncome: reinvest,
    breakEvenMonths: monthlyBenefit > 0 ? Math.ceil(costs / monthlyBenefit) : null,
  };
}

// ------------------------------------------------- Full deal / acquisition

export type DealAnalyzerInput = {
  purchasePrice: number;
  rehab: number;
  /** Loan sizing as a percentage of purchase price. User-entered assumption. */
  ltvPct: number;
  /** User-entered rate assumption (or a rate copied from the live pricing tool). */
  ratePct: number;
  termYears: number;
  interestOnly?: boolean;
  /** Closing costs entered by the user in dollars — no default is assumed. */
  closingCosts: number;
  monthlyRent: number;
  otherIncomeMonthly?: number;
  vacancyPct: number;
  taxesAnnual: number;
  insuranceAnnual: number;
  hoaMonthly?: number;
  managementPct: number;
  maintenancePct: number;
  utilitiesOtherMonthly?: number;
  /** DSCR the user wants to test rent against. */
  targetDscr: number;
  /** Optional refinance / exit assumptions. Omitted or zero => no refi block. */
  refi?: {
    futureValue: number;
    ltvPct: number;
    ratePct: number;
    termYears: number;
    interestOnly?: boolean;
    closingCosts: number;
    holdYears: number;
  };
};

export type DealRefiResult = {
  newLoanAmount: number;
  payoffBalance: number;
  grossProceeds: number;
  closingCosts: number;
  netProceeds: number;
  equityAfterRefi: number;
  newPi: number;
  newPitia: number;
  newDscr: number | null;
  newMonthlyCashFlow: number;
  cashLeftInDeal: number;
  cashOnCashAfterRefi: number | null;
};

export type DealAnalyzerResult = {
  loanAmount: number;
  downPayment: number;
  ltv: number;
  totalProjectCost: number;
  cashInvested: number;
  pi: number;
  pitia: number;
  taxesMonthly: number;
  insuranceMonthly: number;
  hoaMonthly: number;
  annualDebtService: number;
  grossScheduledIncomeMonthly: number;
  vacancyLossMonthly: number;
  effectiveGrossIncomeMonthly: number;
  operatingExpensesMonthly: number;
  noiMonthly: number;
  noiAnnual: number;
  capRate: number;
  dscr: number | null;
  monthlyCashFlow: number;
  annualCashFlow: number;
  cashOnCash: number | null;
  breakevenRentMonthly: number;
  rentNeededForTargetDscr: number;
  rentGapMonthly: number;
  refi: DealRefiResult | null;
};

/**
 * Full acquisition / deal-return analysis built entirely from the primitives
 * above, so DSCR, PITIA, LTV and refinance math match the standalone
 * calculators exactly. Every assumption is supplied by the caller — this
 * module never sources a rate, fee, or program threshold.
 */
export function analyzeDeal(i: DealAnalyzerInput): DealAnalyzerResult {
  const price = Math.max(0, safe(i.purchasePrice));
  const rehab = Math.max(0, safe(i.rehab));
  const loan = round2(price * (Math.min(Math.max(0, safe(i.ltvPct)), 100) / 100));
  const down = round2(price - loan);
  const closing = Math.max(0, safe(i.closingCosts));
  const cashInvested = round2(down + rehab + closing);

  const p = calcPitia({
    loanAmount: loan,
    ratePct: i.ratePct,
    termYears: i.termYears,
    interestOnly: i.interestOnly ?? false,
    taxesAnnual: i.taxesAnnual,
    insuranceAnnual: i.insuranceAnnual,
    hoaMonthly: i.hoaMonthly ?? 0,
  });

  const rent = Math.max(0, safe(i.monthlyRent));
  const otherIncome = Math.max(0, safe(i.otherIncomeMonthly ?? 0));
  const gross = round2(rent + otherIncome);
  const vacancy = round2(gross * (Math.min(Math.max(0, safe(i.vacancyPct)), 100) / 100));
  const egi = round2(gross - vacancy);
  const mgmt = round2(egi * (Math.min(Math.max(0, safe(i.managementPct)), 100) / 100));
  const maint = round2(gross * (Math.min(Math.max(0, safe(i.maintenancePct)), 100) / 100));
  const utilities = Math.max(0, safe(i.utilitiesOtherMonthly ?? 0));
  const opex = round2(mgmt + maint + utilities + p.taxes + p.insurance + p.hoa);
  const noiMonthly = round2(egi - opex);
  const cashFlow = round2(noiMonthly - p.pi);

  const targetRent = rentNeededForDscr(p.pitia, i.targetDscr, rent);

  let refi: DealRefiResult | null = null;
  const r = i.refi;
  if (r && safe(r.futureValue) > 0 && safe(r.ltvPct) > 0) {
    const months = Math.max(0, Math.round(safe(r.holdYears) * 12));
    const payoff = i.interestOnly
      ? loan
      : remainingBalance(loan, i.ratePct, i.termYears, months);
    const newLoan = round2(
      Math.max(0, safe(r.futureValue)) * (Math.min(Math.max(0, safe(r.ltvPct)), 100) / 100),
    );
    const grossProceeds = round2(Math.max(0, newLoan - payoff));
    const refiCosts = Math.max(0, safe(r.closingCosts));
    const net = round2(grossProceeds - refiCosts);
    const newP = calcPitia({
      loanAmount: newLoan,
      ratePct: r.ratePct,
      termYears: r.termYears,
      interestOnly: r.interestOnly ?? false,
      taxesAnnual: i.taxesAnnual,
      insuranceAnnual: i.insuranceAnnual,
      hoaMonthly: i.hoaMonthly ?? 0,
    });
    const newCashFlow = round2(egi - opex - newP.pi);
    const cashLeft = round2(cashInvested - net);
    refi = {
      newLoanAmount: newLoan,
      payoffBalance: payoff,
      grossProceeds,
      closingCosts: refiCosts,
      netProceeds: net,
      equityAfterRefi: round2(Math.max(0, safe(r.futureValue)) - newLoan),
      newPi: newP.pi,
      newPitia: newP.pitia,
      newDscr: newP.pitia > 0 ? round4(rent / newP.pitia) : null,
      newMonthlyCashFlow: newCashFlow,
      cashLeftInDeal: cashLeft,
      cashOnCashAfterRefi: cashLeft > 0 ? round4((newCashFlow * 12) / cashLeft) : null,
    };
  }

  return {
    loanAmount: loan,
    downPayment: down,
    ltv: price > 0 ? round4(loan / price) : 0,
    totalProjectCost: round2(price + rehab + closing),
    cashInvested,
    pi: p.pi,
    pitia: p.pitia,
    taxesMonthly: p.taxes,
    insuranceMonthly: p.insurance,
    hoaMonthly: p.hoa,
    annualDebtService: round2(p.pi * 12),
    grossScheduledIncomeMonthly: gross,
    vacancyLossMonthly: vacancy,
    effectiveGrossIncomeMonthly: egi,
    operatingExpensesMonthly: opex,
    noiMonthly,
    noiAnnual: round2(noiMonthly * 12),
    capRate: price + rehab > 0 ? round4((noiMonthly * 12) / (price + rehab)) : 0,
    dscr: p.pitia > 0 ? round4(rent / p.pitia) : null,
    monthlyCashFlow: cashFlow,
    annualCashFlow: round2(cashFlow * 12),
    cashOnCash: cashInvested > 0 ? round4((cashFlow * 12) / cashInvested) : null,
    breakevenRentMonthly: round2(opex + p.pi),
    rentNeededForTargetDscr: targetRent.requiredMonthlyRent,
    rentGapMonthly: round2(targetRent.requiredMonthlyRent - rent),
    refi,
  };
}
